import { useEffect, useState } from "react";
import type { Rpc } from "../lib/rpc";

interface Account {
  id: string;
  displayName: string;
  username: string;
  avatar?: string | null;
}

interface AccountsResponse {
  accounts: Account[];
  activeAccountId: string | null;
}

interface Props {
  rpc: Rpc;
  electronConnected: boolean;
}

export function AccountSwitcher({ rpc, electronConnected }: Props) {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!electronConnected) {
      setAccounts([]);
      setActiveId(null);
      return;
    }
    refresh();
    const off = rpc.onEvent<Account>("account-switched", (acct) => {
      if (acct?.id) setActiveId(acct.id);
    });
    return off;
  }, [rpc, electronConnected]);

  async function refresh() {
    try {
      const data = await rpc.call<AccountsResponse>("get-accounts");
      setAccounts(data.accounts || []);
      setActiveId(data.activeAccountId || null);
    } catch (err) {
      console.warn("[accounts] fetch failed", err);
    }
  }

  async function pick(id: string) {
    if (id === activeId || busy) {
      setOpen(false);
      return;
    }
    setBusy(true);
    try {
      const r = await rpc.call<{ ok: boolean; error?: string }>("switch-account", id);
      if (r.ok) {
        setActiveId(id);
      } else {
        console.warn("[accounts] switch failed", r.error);
      }
    } catch (err) {
      console.warn("[accounts] switch failed", err);
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  const active = accounts.find((a) => a.id === activeId);

  return (
    <div className={`account-switcher ${open ? "open" : ""}`}>
      <button
        className="account-trigger"
        onClick={() => setOpen((v) => !v)}
        disabled={!electronConnected || accounts.length === 0}
        title={electronConnected ? "Switch account" : "Electron offline"}
      >
        <span className="account-avatar">
          {active?.displayName?.[0]?.toUpperCase() ?? "?"}
        </span>
        <span className="account-name">{active?.displayName ?? "—"}</span>
        <span className="account-chev">▾</span>
      </button>
      {open && (
        <div className="account-dropdown">
          {accounts.map((a) => (
            <button
              key={a.id}
              className={`account-row ${a.id === activeId ? "active" : ""}`}
              onClick={() => pick(a.id)}
              disabled={busy}
            >
              <span className="account-avatar small">
                {a.displayName?.[0]?.toUpperCase() ?? "?"}
              </span>
              <span className="account-row-name">
                <strong>{a.displayName}</strong>
                <small>{a.username}</small>
              </span>
              {a.id === activeId && <span className="account-check">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
